// SK-362: system portal admins. A system portal's admins are the people with an
// Administrator role on it; in collab they act across the system's campuses and
// their left-hand menu lists every campus (COL-4702). Starkiller designates them
// with the same request collab uses to change a member's role: a submission of
// the user to the portal with the role's edge type.
import api from '~/state/api/index';
import ui from '~/state/ducks/ui/actions';
import mapping from '~/utils/mapping.json';
import { toVertexLogoURL } from '~/utils/transform';

const { roles: { by_name: { ADMINISTRATOR, MEMBER } }, vertex_types: { by_name: { USER } } } = mapping;

const userList = (data) => {
    if (data && Array.isArray(data.Body)) return data.Body;
    if (Array.isArray(data)) return data;
    return [];
};

const displayName = ({ name, firstname, lastname }) => (
    name || [firstname, lastname].filter(Boolean).join(' ')
);

export const transformAdmins = data => userList(data)
    .filter(({ role_id }) => Number(role_id) === ADMINISTRATOR)
    .map(user => ({
        id: user.id,
        name: displayName(user),
        email: user.email || '',
        logo_url: toVertexLogoURL({ logo_id: user.logo_id, logo_url: user.logo_url }),
    }))
    .sort((a, b) => a.name.localeCompare(b.name));

export const fetchSystemAdmins = id => async (dispatch) => {
    try {
        const { data } = await dispatch(api.get(`/organizations/${id}/users`, {
            params: { limit: -1 },
        }));
        return transformAdmins(data);
    } catch (error) {
        dispatch(ui.flash.error({ header: 'Failed to fetch the system admins' }, error));
        return [];
    }
};

export const searchUsers = query => async (dispatch) => {
    if (!query || query.length < 3) return [];
    try {
        const { data } = await dispatch(api.get('/search', {
            params: {
                types: USER,
                email_terms: true,
                terms: query,
                limit: 15,
            },
        }));
        return (data.results || []).map(({
            id, name, logo_id, logo_url,
        }) => ({
            id,
            name,
            logo_url: toVertexLogoURL({ logo_id, logo_url }),
        }));
    } catch (error) {
        dispatch(ui.flash.error({ header: 'User search failed' }, error));
        return [];
    }
};

const setRole = (portalId, userId, authorId, role) => api.post('/submissions', {
    data: {
        author_id: authorId,
        to: [portalId],
        edge_type_id: role,
        vertex_id: userId,
    },
});

export const addSystemAdmin = (portalId, userId, authorId) => async (dispatch) => {
    try {
        await dispatch(setRole(portalId, userId, authorId, ADMINISTRATOR));
        dispatch(ui.flash.success({ header: 'System admin added' }));
        return true;
    } catch (error) {
        dispatch(ui.flash.error({ header: 'Failed to add the system admin' }, error));
        return false;
    }
};

// Removing a system admin keeps them in the portal as a member.
export const removeSystemAdmin = (portalId, userId, authorId) => async (dispatch) => {
    try {
        await dispatch(setRole(portalId, userId, authorId, MEMBER));
        dispatch(ui.flash.success({ header: 'System admin removed' }));
        return true;
    } catch (error) {
        dispatch(ui.flash.error({ header: 'Failed to remove the system admin' }, error));
        return false;
    }
};
